<template>
  <section class="activity-stage">
    <div id="cards">
      <flippable-card
        v-for="step in currentRound.steps"
        :is-flipped="step.stepIndex < progress.stepIndex"
        :key="step.stepIndex"
        class="card"
      >
        <div slot="card-back" class="back-side">
          <img :src="backOfCardPath" />
        </div>
        <div slot="card-front" class="front-side">
          <img :src="step.img" />
        </div>
      </flippable-card>
    </div>
    <div id="answers">
      <div class="answer" v-for="step in currentRound.steps" :key="`answer-${step.stepIndex}`">
        <span>{{ step.toShow }}</span>
        <blinking-cursor-text
          :text="step.toType"
          :text-step="step.stepIndex"
        ></blinking-cursor-text>
      </div>
    </div>
    <slot name="keyboard"></slot>
  </section>
</template>

<script>
import { TweenMax, TimelineLite } from 'gsap';

import AssetLoader from 'mixins/AssetLoader';
import ErrorLogger from 'mixins/ErrorLogger';
import TypingActivityHelper from 'mixins/TypingActivityHelper';
import ActivityManager from 'mixins/ActivityManager';

import FlippableCard from 'components/activity/FlippableCard.vue';
import BlinkingCursorText from 'components/text-displays/BlinkingCursorText.vue';

import backOfCardPath from 'assets/images/face-flip-card-back.png';

export default {
  mixins: [AssetLoader, TypingActivityHelper, ActivityManager, ErrorLogger],

  components: {
    FlippableCard,
    BlinkingCursorText
  },

  data() {
    return {
      activityName: 'RhymeFlip',
      sfx: null,
      backOfCardPath
    };
  },

  beforeDestroy() {
    TweenMax.killAll();
  },

  methods: {
    async loadActivity() {
      const roundSetup = this.getActivityState();
      this.createRounds(roundSetup);
      await this.loadImages();
      this.sfx = await this.loadSounds();
    },

    getActivityState() {
      const {
        rounds,
        keyboard: { characters }
      } = this.activityData;

      return {
        rounds,
        instructions: {
          written: characters
            ? 'Hold the target. Type the letter combos'
            : 'Type each letter combo.',
          voice: `${this.voiceInstructionsPrefix}/rhymeflip${
            characters ? '' : 'nth'
          }-${this.getRandomVoice()}-1.mp3`,
          delay: 3
        }
      };
    },

    getKeyboardConfig() {
      const { keyboardType, roofs } = this.activityData.keyboard;
      return {
        keyboardType,
        roofs
      };
    },

    startActivity() {
      this.setRound();
    },

    async loadImages() {
      this.rounds.forEach(({ steps: { img } }) => this.queueImage(img));

      return this.downloadImages();
    },

    async loadSounds() {
      this.rounds.forEach(({ steps }) => steps.forEach(({ audio }) => this.queueAudio(audio)));

      const cache = await this.downloadAudio();

      return this.rounds.map(({ steps }) => steps.map(({ audio }) => cache.get(audio)));
    },

    setRound() {
      this.setupNewRound();
      this.showKeyboard({ enable: true });
      this.errorLoggerStart();
      TweenMax.delayedCall(0.5, () => {
        TweenMax.to('.activity-stage', 0.5, { opacity: 1 });
      });
    },

    handleCorrectKey() {
      this.sndType();
      this.incrementTypingIndex();
      const { roundIndex, stepIndex } = this.progress;

      if (this.isEndOfCurrentSentence) {
        this.sndBell();
        if (this.sfx[roundIndex][stepIndex]) {
          this.sfx[roundIndex][stepIndex].play();
        }
        this.setCurrentStep();
      }

      if (this.isEndOfRound) {
        this.handleRoundEnd();
      }
    },

    handleRoundEnd() {
      if (this.isLastRound) {
        TweenMax.delayedCall(1, this.completeActivity);
      } else {
        const t = new TimelineLite();
        t.delay(1.3)
          .to('.activity-stage', 1, { opacity: 0 })
          .call(this.setRound);
      }
    }
  }
};
</script>

<style lang="scss" scoped>
#cards {
  position: relative;
  display: inline-flex;
  justify-content: space-evenly;
  margin: 3% 6% 5% 6%;
  .card {
    text-align: center;
    margin: 2% 0.5% 0 0.5%;

    img {
      width: 100%;
    }
  }
}

#answers {
  position: relative;
  display: flex;
  justify-content: space-evenly;
  text-align: center;
  top: -50px;

  @supports (-ms-ime-align: auto) {
    & {
      justify-content: space-around !important;
    }
  }

  .answer {
    font-size: 2vw;
    margin: 0 0.5%;
    span,
    p {
      display: inline-block;
      padding: 0;
      margin: 0;
    }
  }
}
</style>
